import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Stack,
    Text,
    IconButton,
    TooltipHost
} from '@fluentui/react';
import {
    Dropdown,
    Option,
    makeStyles
} from '@fluentui/react-components';
import './DashboardFilters.css';

const useStyles = makeStyles({
    dropdown: {
        minWidth: '250px'
    }
});

/**
 * DashboardFilters - Dynamic filters component for analytics dashboard
 * 
 * Loads filter options from Redux (flows, queues, agents)
 * and allows user to select values to filter data.
 * 
 * @param {string} tenantId - Tenant ID
 * @param {string} scope - Data scope (e.g., 'default')
 * @param {function} onFilterChange - Callback when filters change
 * @param {object} initialFilters - Initial filter values to restore state
 */
const DashboardFilters = ({ tenantId, scope, onFilterChange, initialFilters = {} }) => {
    const styles = useStyles();
    const { t } = useTranslation();

    // Get data from Redux
    const callFlows = useSelector(state => state.reducer.CALL_FLOWS);
    const queues = useSelector(state => state.reducer.QUEUES);
    const agents = useSelector(state => state.reducer.AGENTS);
    // Agents IA : considérés comme des flows côté CDR (FLOW_ID = id agent IA).
    const agentAIs = useSelector(state => state.reducer.AGENT_AIS);

    // State for selected values - store only keys
    const [selectedFlows, setSelectedFlows] = useState([]);
    const [selectedQueues, setSelectedQueues] = useState([]);
    const [selectedAgents, setSelectedAgents] = useState([]);

    // Track if initial restore has been done
    const isInitialMount = useRef(true);

    // Helper function to convert object or array to array
    const toArray = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        // If it's an object, convert to array of values
        return Object.values(data);
    };

    // ⚠️ InitComponent charge flows, files, agents et agents IA pour TOUS les scopes du tenant
    // et les fusionne dans un seul objet Redux (côté Provisioning, la branche `scope === "default"`
    // ne filtre même pas et renvoie tout le tenant). Sans re-filtrage ICI, le rapport d'une entité
    // proposait les flows, files et agents des AUTRES entités : sur `default`, la liste affichait
    // « Heedify-UK » ou « 🤖 Heedify UK AIR », qui n'y produiront jamais un seul appel.
    //
    // Un objet sans champ `scope` appartient à `default` — même règle que la liste des call flows
    // (`flow.scope || "default"`) et que les colonnes des tableaux par flow/file.
    const currentScope = String(scope || 'default');
    const belongsToScope = React.useCallback(
        (e) => String(e?.scope || 'default') === currentScope,
        [currentScope]
    );

    /**
     * Une sélection ENREGISTRÉE (filtres propres d'un widget) peut porter sur un flow, une file
     * ou un agent qui n'appartient pas — ou plus — à l'entité : config faite quand la liste
     * proposait encore tout le tenant, ou entité supprimée depuis. Cette valeur continue de
     * FILTRER la donnée côté backend.
     *
     * Elle n'était affichée nulle part : le dropdown ne connaissait pas la clé (donc rien de
     * coché), et les tags « Active filters » faisaient `return ... : null` sur les clés
     * inconnues. Le widget se retrouvait filtré sur deux flows d'une autre entité, sans que
     * l'écran de config ne montre le moindre filtre — et sans aucun moyen de l'enlever.
     *
     * On rend donc toujours la sélection : nommée depuis la liste COMPLÈTE du tenant si on
     * l'y retrouve, à défaut par sa clé brute, et marquée comme hors entité.
     */
    const withOrphans = (options, selected, allOptions) => {
        const known = new Set(options.map(o => o.key));
        const orphans = (selected || []).filter(k => !known.has(String(k))).map(k => {
            const found = allOptions.find(o => o.key === String(k));
            return { key: String(k), text: `⚠️ ${found ? found.text : k} — ${t('OUTSIDE_ENTITY') || 'hors entité'}` };
        });
        return [...options, ...orphans];
    };

    // Build dropdown options from Redux data - MEMOIZED to prevent recreation.
    // Le filtre "Flows" liste les call flows ET les agents IA (préfixe 🤖) :
    // les deux filtrent c.FLOW_ID côté backend (un appel IA a FLOW_ID = id agent IA).
    const flowOptions = useMemo(() => {
        const asOption = (flow) => ({
            key: String(flow.flowId || flow.id || flow.flowKey),
            text: flow.flowName || flow.displayName || flow.name || flow.id
        });
        const asIaOption = (a) => ({
            key: String(a.id),
            text: `🤖 ${a.displayName || a.agentPersonality?.name || a.id}`
        });
        const all = [...toArray(callFlows).map(asOption), ...toArray(agentAIs).map(asIaOption)];
        const inScope = [
            ...toArray(callFlows).filter(belongsToScope).map(asOption),
            ...toArray(agentAIs).filter(belongsToScope).map(asIaOption)
        ];
        return withOrphans(inScope, selectedFlows, all);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callFlows, agentAIs, belongsToScope, selectedFlows, t]);

    const queueOptions = useMemo(() => {
        const asOption = (queue) => ({
            key: String(queue.queueName || queue.displayName || queue.name || queue.id),
            text: queue.queueName || queue.displayName || queue.name || queue.id
        });
        return withOrphans(
            toArray(queues).filter(belongsToScope).map(asOption),
            selectedQueues,
            toArray(queues).map(asOption)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queues, belongsToScope, selectedQueues, t]);

    const agentOptions = useMemo(() => {
        const asOption = (agent) => ({
            key: String(agent.id || agent.agentKey || agent.odataId),
            text: agent.displayName || agent.name || agent.id
        });
        return withOrphans(
            toArray(agents).filter(belongsToScope).map(asOption),
            selectedAgents,
            toArray(agents).map(asOption)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agents, belongsToScope, selectedAgents, t]);

    /**
     * Une sélection est un ENSEMBLE : chaque clé au plus une fois.
     *
     * Rien ne le garantissait, et un doublon ne se voyait pas — les tags sont rendus avec
     * `key={clé}`, or React ne garde qu'un enfant par clé : une liste de 6 entrées dont 2
     * distinctes s'affichait comme 2 filtres. Le compte enregistré et le compte affiché
     * divergeaient donc silencieusement, et le doublon repartait tel quel à la sauvegarde.
     */
    const uniqueKeys = (list) => [...new Set((Array.isArray(list) ? list : []).map(String))];

    /**
     * Restore state from initialFilters ONLY on initial mount
     */
    useEffect(() => {
        if (!isInitialMount.current) return;
        isInitialMount.current = false;

        if (initialFilters.flowKey && Array.isArray(initialFilters.flowKey)) {
            setSelectedFlows(uniqueKeys(initialFilters.flowKey));
        }
        if (initialFilters.queueKey && Array.isArray(initialFilters.queueKey)) {
            setSelectedQueues(uniqueKeys(initialFilters.queueKey));
        }
        if (initialFilters.agentKey && Array.isArray(initialFilters.agentKey)) {
            setSelectedAgents(uniqueKeys(initialFilters.agentKey));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Notify parent when filters change
     */
    useEffect(() => {
        // Dédoublonné À LA SOURCE : ce qui remonte au parent est ce qui sera enregistré.
        // Un profil qui contenait déjà des doublons est donc nettoyé dès la première
        // sauvegarde du widget, sans manipulation particulière.
        const filters = {};

        if (selectedFlows?.length > 0) {
            filters.flowKey = uniqueKeys(selectedFlows);
        }
        if (selectedQueues?.length > 0) {
            filters.queueKey = uniqueKeys(selectedQueues);
        }
        if (selectedAgents?.length > 0) {
            filters.agentKey = uniqueKeys(selectedAgents);
        }

        onFilterChange(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFlows, selectedQueues, selectedAgents]);

    /**
     * Reset all filters
     */
    const handleClearFilters = () => {
        setSelectedFlows([]);
        setSelectedQueues([]);
        setSelectedAgents([]);
    };

    const hasActiveFilters = (selectedFlows?.length > 0) || (selectedQueues?.length > 0) || (selectedAgents?.length > 0);

    return (
        <div style={{ display: 'contents' }}>
            {/* Flow Filter - Multi-select */}
            <Dropdown
                className={styles.dropdown}
                multiselect={true}
                placeholder={t('FLOW_FILTER_PLACEHOLDER')}
                value=""
                selectedOptions={selectedFlows}
                onOptionSelect={(e, data) => {
                    setSelectedFlows(data.selectedOptions);
                }}
                style={{ minWidth: '180px', width: '180px', height: '32px' }}
            >
                {flowOptions.map((option) => (
                    <Option key={option.key} value={option.key}>
                        {option.text}
                    </Option>
                ))}
            </Dropdown>

            {/* Queue Filter - Multi-select */}
            <Dropdown
                className={styles.dropdown}
                multiselect={true}
                placeholder={t('QUEUE_FILTER_PLACEHOLDER')}
                value=""
                selectedOptions={selectedQueues}
                onOptionSelect={(e, data) => {
                    setSelectedQueues(data.selectedOptions);
                }}
                style={{ minWidth: '180px', width: '180px', height: '32px' }}
            >
                {queueOptions.map((option) => (
                    <Option key={option.key} value={option.key}>
                        {option.text}
                    </Option>
                ))}
            </Dropdown>

            {/* Agent Filter - Multi-select */}
            <Dropdown
                className={styles.dropdown}
                multiselect={true}
                placeholder={t('AGENT_FILTER_PLACEHOLDER')}
                value=""
                selectedOptions={selectedAgents}
                onOptionSelect={(e, data) => {
                    setSelectedAgents(data.selectedOptions);
                }}
                style={{ minWidth: '180px', width: '180px', height: '32px' }}
            >
                {agentOptions.map((option) => (
                    <Option key={option.key} value={option.key}>
                        {option.text}
                    </Option>
                ))}
            </Dropdown>

            {/* Display active filters - nouvelle ligne en dessous */}
            {hasActiveFilters && (
                <div style={{ width: '100%', marginTop: '10px' }}>
                    <Stack horizontal tokens={{ childrenGap: 10 }} wrap>
                        <Text variant="small" styles={{ root: { color: '#605e5c', paddingTop: 5 } }}>
                            {t('ACTIVE_FILTERS')}
                        </Text>
                        {selectedFlows.map(flowKey => {
                            // Jamais `null` sur une clé inconnue : un filtre actif doit rester
                            // visible ET supprimable, même si son flow a disparu de l'entité.
                            const flow = flowOptions.find(f => f.key === flowKey) || { text: flowKey };
                            return flow ? (
                                <Stack key={flowKey} horizontal tokens={{ childrenGap: 5 }} className="filter-tag filter-tag-flow">
                                    <Text variant="small" styles={{ root: { color: 'white' } }}>
                                        {t('FLOW')}: {flow.text}
                                    </Text>
                                    <button
                                        className="filter-tag-remove"
                                        onClick={() => setSelectedFlows(prev => prev.filter(k => k !== flowKey))}
                                        title="Supprimer ce filtre"
                                    >
                                        ×
                                    </button>
                                </Stack>
                            ) : null;
                        })}
                        {selectedQueues.map(queueKey => {
                            const queue = queueOptions.find(q => q.key === queueKey) || { text: queueKey };
                            return queue ? (
                                <Stack key={queueKey} horizontal tokens={{ childrenGap: 5 }} className="filter-tag filter-tag-queue">
                                    <Text variant="small" styles={{ root: { color: 'white' } }}>
                                        {t('QUEUE')}: {queue.text}
                                    </Text>
                                    <button
                                        className="filter-tag-remove"
                                        onClick={() => setSelectedQueues(prev => prev.filter(k => k !== queueKey))}
                                        title="Supprimer ce filtre"
                                    >
                                        ×
                                    </button>
                                </Stack>
                            ) : null;
                        })}
                        {selectedAgents.map(agentKey => {
                            const agent = agentOptions.find(a => a.key === agentKey) || { text: agentKey };
                            return agent ? (
                                <Stack key={agentKey} horizontal tokens={{ childrenGap: 5 }} className="filter-tag filter-tag-agent">
                                    <Text variant="small" styles={{ root: { color: 'white' } }}>
                                        {t('AGENT')}: {agent.text}
                                    </Text>
                                    <button
                                        className="filter-tag-remove"
                                        onClick={() => setSelectedAgents(prev => prev.filter(k => k !== agentKey))}
                                        title="Supprimer ce filtre"
                                    >
                                        ×
                                    </button>
                                </Stack>
                            ) : null;
                        })}
                    </Stack>
                </div>
            )}
        </div>
    );
};

export default DashboardFilters;
